import { generatePath } from 'react-router-dom';
import URL from '../../../../constants/api-constants';
import http from '../../../../store/http/http-common';
import { ChangeControllerSettingsAction } from '../../../../enums/action';
import { toast } from 'react-toastify';
import { IControllerSettings } from '../../../../types/controller-types';
import { number, object } from 'yup';
import i18n from '../../../../infrastructure/i18n';
import ValidationAction from '../../../../types/validation-action';

export const changeControllerSettings = async (
  controller_id: string,
  action: ChangeControllerSettingsAction,
  newValue: string,
) => {
  try {
    await http.put(
      generatePath(URL.CONTROLLER_SETTINGS, {
        id: controller_id,
      }),
      { action, value: newValue },
    );
    toast.success('Action successfully executed');
  } catch (error) {
    toast.error(error.message);
  }
};

export type ChangeControllerSettingsActionType = ValidationAction & {
  inputType: React.InputHTMLAttributes<HTMLInputElement>['type'];
  actionName: ChangeControllerSettingsAction;
  actionField: keyof Omit<IControllerSettings, 'id'>;
  labelText: string;
};

export type ChangeControllerSettingsButtonActionType = Omit<
  ChangeControllerSettingsActionType,
  'inputType' | 'actionField'
> & { value: string };

export const CONTROLLER_SETTINGS_LIMITS = {
  COOLING_SPREAD_CONTROL: {
    MIN: 0,
    MAX: 1000,
  },
  PID_P: {
    MIN: 1,
    MAX: 400,
  },
  PID_I: {
    MIN: 0,
    MAX: 1000,
  },
  PID_D: {
    MIN: 0,
    MAX: 1000,
  },
};

export const settingsActions: ChangeControllerSettingsActionType[] = [
  {
    inputType: 'number',
    actionName: ChangeControllerSettingsAction.SET_COOLING_SPREAD_CONTROL,
    actionField: 'cooling_spread_control',
    labelText: i18n.t('controllers.settings.cooling_spread_control'),
    validationSchema: object({
      cooling_spread_control: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CONTROLLER_SETTINGS_LIMITS.COOLING_SPREAD_CONTROL.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CONTROLLER_SETTINGS_LIMITS.COOLING_SPREAD_CONTROL.MIN }),
        )
        .max(
          CONTROLLER_SETTINGS_LIMITS.COOLING_SPREAD_CONTROL.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CONTROLLER_SETTINGS_LIMITS.COOLING_SPREAD_CONTROL.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
];

export const settingsPidActions: ChangeControllerSettingsActionType[] = [
  {
    inputType: 'number',
    actionName: ChangeControllerSettingsAction.PID_GAINS_CHANGE_P,
    actionField: 'pid_p',
    labelText: i18n.t('controllers.settings.pid_p'),
    validationSchema: object({
      pid_p: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CONTROLLER_SETTINGS_LIMITS.PID_P.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CONTROLLER_SETTINGS_LIMITS.PID_P.MIN }),
        )
        .max(
          CONTROLLER_SETTINGS_LIMITS.PID_P.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CONTROLLER_SETTINGS_LIMITS.PID_P.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
  {
    inputType: 'number',
    actionName: ChangeControllerSettingsAction.PID_GAINS_CHANGE_I,
    actionField: 'pid_i',
    labelText: i18n.t('controllers.settings.pid_i'),
    validationSchema: object({
      pid_i: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CONTROLLER_SETTINGS_LIMITS.PID_I.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CONTROLLER_SETTINGS_LIMITS.PID_I.MIN }),
        )
        .max(
          CONTROLLER_SETTINGS_LIMITS.PID_I.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CONTROLLER_SETTINGS_LIMITS.PID_I.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
  {
    inputType: 'number',
    actionName: ChangeControllerSettingsAction.PID_GAINS_CHANGE_D,
    actionField: 'pid_d',
    labelText: i18n.t('controllers.settings.pid_d'),
    validationSchema: object({
      pid_d: number()
        .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
        .min(
          CONTROLLER_SETTINGS_LIMITS.PID_D.MIN,
          i18n.t('errors.numberValue.min', '', { minNum: CONTROLLER_SETTINGS_LIMITS.PID_D.MIN }),
        )
        .max(
          CONTROLLER_SETTINGS_LIMITS.PID_D.MAX,
          i18n.t('errors.numberValue.max', '', { maxNum: CONTROLLER_SETTINGS_LIMITS.PID_D.MAX }),
        )
        .required(i18n.t('errors.requiredValue')),
    }),
  },
];

export const settingsButtonActions: ChangeControllerSettingsButtonActionType[] = [
  {
    actionName: ChangeControllerSettingsAction.SET_SHUNT_TRIP,
    labelText: 'Shunt Trip 1',
    value: '1',
  },
  {
    actionName: ChangeControllerSettingsAction.SET_SHUNT_TRIP,
    labelText: 'Shunt Trip 2',
    value: '2',
  },
  {
    actionName: ChangeControllerSettingsAction.SET_SHUNT_TRIP,
    labelText: 'Shunt Trip 3',
    value: '3',
  },
  {
    actionName: ChangeControllerSettingsAction.SET_SHUNT_TRIP,
    labelText: 'Shunt Trip 4',
    value: '4',
  },
];

export const settingsResetButtonActions: Omit<ChangeControllerSettingsButtonActionType, 'value'>[] = [
  {
    actionName: ChangeControllerSettingsAction.RESET_TC_ASSIGNEMENTS,
    labelText: 'Reset TC Assignments',
  },
  {
    actionName: ChangeControllerSettingsAction.CLEAR_ALL_CHANNELS_NAMES,
    labelText: 'Clear All Channel Names',
  },
];

export const settingsResetFaultButtonActions: Omit<ChangeControllerSettingsButtonActionType, 'value'>[] = [
  {
    actionName: ChangeControllerSettingsAction.RESET_FAULT,
    labelText: 'Fault Reset',
  },
];

export const settingsFactoryResetButtonActions: Omit<ChangeControllerSettingsButtonActionType, 'value'>[] = [
  {
    actionName: ChangeControllerSettingsAction.FACTORY_RESET,
    labelText: 'FACTORY RESET',
  },
];
